/**
 * paywall-adapter.ts — 支付域 真实 DTO → 设计稿 mock 形状塑形（接真用，页面不动）
 *
 * 真实后端（ADM-PAY-S001~S003）把"支付页面"与"Paywall 渠道配置"拆成两个 snake_case DTO：
 *  - PaymentPageResponse  = admin/payment_pages 一行（页面本体：id / scenario / name / is_built_in / is_active）
 *  - PaywallConfigResponse = 该页面 × 结算通道（`SETTLEMENT_CHANNELS`）一行（placement；渠道行无启停、无排序键）
 * 而设计稿（PaywallPage.tsx）消费的是 camelCase 富集形状：
 *  - PaywallPageItem    = 支付页面列表/详情行（system / status 中文 / channels[] / products[] / updated / by）
 * 本 adapter 只做"塑形 + 字段映射 + 缺字段补默认"，不发请求（取数复用 ~/hooks/use-payments.ts）。
 * 注：产品「业务效果」（coin/bonus 录入）已迁至 product_metadata（use-product-metadata.ts + ProductMirror tab），
 * 本 adapter 不再产出 Adapty 镜像产品行（旧 adaptPayProduct/PayProduct 已退役）。
 *
 * 字段映射（DTO → mock）：
 *  - is_built_in (boolean)        → system (boolean) 原样 + status 文案无关
 *  - is_active  (boolean)         → status (string) '上架' | '下架'（中文）
 *  - payment_method（`SETTLEMENT_CHANNELS`：IAP / STRIPE / 已登记框架通道）→ channels 小写渠道码（'iap' / 'stripe' /
 *    'payermax'）；闭集外的取值（含已移除的 PAYPAL）过滤丢弃
 *  - created_at / updated_at      → updated (string)：取 updated_at（无则 created_at）
 *
 * 真实 API 普遍缺设计稿的"聚合/关联名"字段：
 *  - products[]（页面内产品档位明细）：真 API 不返回 → 默认 []，经 opts.products 注入（N+1）
 *  - by（最后操作人名）：真 API 无 → 默认 '—'，经 opts.by 注入（N+1 admin_audit_logs / admin_users）
 */

import {
  type PaymentPageResponse,
  type PaywallConfigResponse,
  SETTLEMENT_CHANNELS,
  type SettlementChannel,
  isSettlementChannel,
} from '@novel-admin/shared';

// ── 设计稿目标形状（与 PaywallPage.tsx 内部 interface 同构，集中定义供页面/adapter 共用）──

/** 支付页面里的一个产品档位（系统预置 Paywall 内嵌结构，对齐 PaywallPage.tsx）。 */
export interface PaywallPageProduct {
  id: string;
  name: string;
  price: number;
  type: string;
  coin: number;
  bonus: number;
}

/** 单个支付页面（Paywall），对齐 PaywallPage.tsx 的 PaywallPageItem。 */
export interface PaywallPageItem {
  id: string;
  /** payment_pages.scenario（= scenario 闭集值）；用于 upsertPaywall 关联 + 删除确认提示 */
  scenario: string;
  /** 归属 App（UUID）；新建跨 App 版本 / 删除引用校验上浮用 */
  appId: string;
  name: string;
  system: boolean;
  status: string;
  channels: string[];
  /**
   * 各渠道当前已存的 Adapty placement_id（按渠道独立存储，对齐设计稿 placements state）。
   * key = 小写渠道码（`PAYWALL_CHANNEL_IDS`）；缺渠道则无该 key（页面渲染时回退到期望 placement 占位）。
   */
  placements: Record<string, string>;
  /**
   * 该页全部渠道行的 `max(updated_at)`（A8 §7.5 scenario 级乐观锁令牌），PUT paywalls 时作
   * `expected_updated_at` 回传。`null` = 该页还没有任何渠道行 / 渠道配置未拉到，此时不带令牌。
   */
  placementsToken: string | null;
  updated: string;
  by: string;
  products: PaywallPageProduct[];
  /**
   * 该页渠道配置（listPaywalls）是否拉取失败（F5）。true = listPaywalls 请求 reject（瞬时 401/500 等），
   * channels/placements 不可信（非「确无渠道」而是「未知」）→ 页面必须禁用该页的 placement 写 / publish，
   * 避免运营在残缺数据上 publish 覆盖真实配置。仅显式成功的空响应才 false + channels=[]。
   */
  paywallsError: boolean;
}

/**
 * 支付页渠道码（小写）= 结算通道闭集 `SETTLEMENT_CHANNELS` 逐项小写，次序同闭集（IAP → STRIPE → 已登记框架通道）。
 * 渠道卡片、placement 草稿、「剩余可用渠道」计数都按它遍历：登记新的框架通道时自动出现，页面不写死通道值。
 */
export const PAYWALL_CHANNEL_IDS: readonly string[] = SETTLEMENT_CHANNELS.map((channel) =>
  channel.toLowerCase(),
);

/** 小写渠道码 → 结算通道；不在闭集内返 null。 */
export function settlementChannelOfPaywallChannelId(channelId: string): SettlementChannel | null {
  const channel = channelId.toUpperCase();
  return isSettlementChannel(channel) ? channel : null;
}

/** is_active → 中文上下架文案。 */
function activeLabel(isActive: boolean): string {
  return isActive ? '上架' : '下架';
}

/**
 * 渠道行展示次序 = 结算通道闭集次序（IAP → STRIPE → 已登记框架通道）。渠道行响应不带排序键（A8 §7.5 item
 * 为 5 字段闭集，本端点不承载排序），次序只能由闭集给出。闭集外取值排末尾（随后被过滤）。
 */
function byChannelOrder(a: PaywallConfigResponse, b: PaywallConfigResponse): number {
  const rank = (m: string) => {
    const i = (SETTLEMENT_CHANNELS as readonly string[]).indexOf(m);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return rank(a.payment_method) - rank(b.payment_method);
}

/**
 * adaptPaywallChannels — PaywallConfigResponse[] → 设计稿 channels（小写渠道码）。
 * 渠道行已无 `is_active` 启停（dev-docs #530）：**已配置 = adapty_placement_id 非空**，
 * 未填 placement 的行不算已配置、不参与 serving 候选。仅保留结算通道闭集内的行（闭集外取值丢弃）；
 * 按结算通道闭集次序取小写码（'iap' / 'stripe' / 'payermax'），去重。
 */
export function adaptPaywallChannels(configs: PaywallConfigResponse[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of [...configs].sort(byChannelOrder)) {
    if (c.adapty_placement_id === null) continue;
    if (!isSettlementChannel(c.payment_method)) continue; // 闭集外渠道过滤
    const code = c.payment_method.toLowerCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/** 单个支付页面塑形的可选补充数据源（缺字段经此注入；不传则用合理默认）。 */
export interface AdaptPaymentPageOptions {
  /**
   * 该页面的 Paywall 渠道配置（决定 channels[]）。
   * 仅在 listPaywalls **显式成功**时传（含成功返空数组 → channels=[]）；请求失败时**不要传 []**，
   * 改用 paywallsError=true（F5：失败静默当空配置会让运营在残缺数据上 publish 覆盖）。
   */
  paywalls?: PaywallConfigResponse[];
  /** 该页 listPaywalls 是否拉取失败（F5）；true → channels/placements 不可信、页面禁写该页 placement / publish。 */
  paywallsError?: boolean;
  /**
   * 该页面内产品档位明细（真 API 不返回，产品本体由 Adapty 承接）。
   * 经 N+1 / Adapty 镜像注入；不传则 products=[]。
   */
  products?: PaywallPageProduct[];
  /**
   * 最后操作人显示名（真 API 无此关联名，需 N+1 查 admin_audit_logs + admin_users）。
   * 不传则 '—'。
   */
  by?: string;
}

/**
 * adaptPaywallPlacements — PaywallConfigResponse[] → 各渠道已存 placement_id（{iap?,stripe?,payermax?}）。
 * 仅取结算通道闭集内的行，同渠道取首行。
 * placement 为空的行**不进 map**——本 map 的语义是「已存的 placement_id」，未配 placement 的渠道
 * 应回显为空而不是空串（C 端已放开该列可空）。
 */
export function adaptPaywallPlacements(configs: PaywallConfigResponse[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of [...configs].sort(byChannelOrder)) {
    if (!isSettlementChannel(c.payment_method)) continue;
    if (c.adapty_placement_id === null) continue;
    const code = c.payment_method.toLowerCase();
    if (out[code] === undefined) out[code] = c.adapty_placement_id;
  }
  return out;
}

/**
 * adaptPaywallToken — PaywallConfigResponse[] → scenario 级乐观锁令牌（全部行的 max(updated_at)）。
 * **取原始全集、不按结算通道闭集过滤**：服务端的 max 不按 payment_method 过滤，按裁剪后的行算
 * 会在出现闭集外的行时恒小于服务端值 → 保存必冲突、刷新也没用。ISO 字符串同形态可直接按字典序比较。
 */
export function adaptPaywallToken(configs: PaywallConfigResponse[]): string | null {
  let token: string | null = null;
  for (const c of configs) {
    if (token === null || c.updated_at > token) token = c.updated_at;
  }
  return token;
}

/**
 * adaptPaymentPage — PaymentPageResponse → 设计稿 PaywallPageItem。
 * 缺字段（products / by / channels）经 opts 注入，否则取合理默认（[] / '—' / []）。
 * channels（上架渠道）与 placements（各渠道已存 placement_id，含草稿）均自 opts.paywalls 派生。
 */
export function adaptPaymentPage(
  page: PaymentPageResponse,
  opts: AdaptPaymentPageOptions = {},
): PaywallPageItem {
  return {
    id: page.id,
    scenario: page.scenario,
    appId: page.app_id,
    name: page.name,
    system: page.is_built_in,
    status: activeLabel(page.is_active),
    channels: opts.paywalls ? adaptPaywallChannels(opts.paywalls) : [],
    placements: opts.paywalls ? adaptPaywallPlacements(opts.paywalls) : {},
    placementsToken: opts.paywalls ? adaptPaywallToken(opts.paywalls) : null,
    // updated_at 为主，无则回退 created_at（DTO 两者均非空，回退仅作防御）
    updated: page.updated_at || page.created_at,
    by: opts.by ?? '—',
    products: opts.products ?? [],
    paywallsError: opts.paywallsError ?? false,
  };
}

/**
 * adaptPaymentPages — 批量塑形。perPage 用 page.id 取该页面专属补充数据，
 * 让上层（PaywallPage）按 N+1（listPaywalls(id) / 产品镜像 / audit）逐页注入。
 */
export function adaptPaymentPages(
  pages: PaymentPageResponse[],
  perPage: (page: PaymentPageResponse) => AdaptPaymentPageOptions = () => ({}),
): PaywallPageItem[] {
  return pages.map((p) => adaptPaymentPage(p, perPage(p)));
}
